import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./PreRegistrationListPage.module.css";
import { useTournamentStore } from '../../../service/useTournamentStore';
import {
  useConvertPreRegistrations,
  useDeleteRegistration,
  useExportPreRegistrations,
  useGetPreRegistrations,
} from "../../../components/public/registrationHooks";
import { useGetHeatsForTournament } from "../../../components/heat/heatHooks";
import { useGetPreRegistrationConfig } from "../hooks/tournamentHooks";
import type { PreRegistration } from '@iziping/common';
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_CACHE } from '@/model/endpoints';
import ConfirmationModal from "../../../components/shared/ConfirmationModal";
import { useNotification } from '@iziping/ui';
import { FileSpreadsheet, Pencil, Search, UserMinus } from "lucide-react";
import { InputIcon } from "@iziping/ui";

const PreRegistrationListPage: React.FC = () => {
  const { t } = useTranslation();
  const { selectedTournament } = useTournamentStore();
  const tournamentId = Number(selectedTournament?.id);
  const queryClient = useQueryClient();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: allHeats } = useGetHeatsForTournament(tournamentId);
  const { data: preRegistrations, isLoading, isError } = useGetPreRegistrations(tournamentId);
  const { data: preRegistrationConfig } = useGetPreRegistrationConfig(tournamentId);
  const registrationUUID = preRegistrationConfig?.preRegistrationConfig?.registrationId;
  const { mutate: deleteRegistration } = useDeleteRegistration();
  const { mutate: convertPreRegistrations, isPending: isConverting } = useConvertPreRegistrations();
  const { mutateAsync: exportPreRegistrations, isPending: isExporting } = useExportPreRegistrations();
  const { notify } = useNotification();

  if (!selectedTournament)
    return (
      <div className={`page ${styles.page}`}>
        <p>{t("preRegistration.no.tournament")}</p>
      </div>
    );
  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error fetching data</div>;

  const normalizedSearch = searchTerm.toLowerCase();
  const filteredRegistrations = (preRegistrations ?? []).filter((reg: PreRegistration) => {
    if (!normalizedSearch) return true;
    return (
      reg.licence?.toLowerCase().includes(normalizedSearch) ||
      reg.lastname?.toLowerCase().includes(normalizedSearch) ||
      reg.firstname?.toLowerCase().includes(normalizedSearch) ||
      reg.email?.toLowerCase().includes(normalizedSearch)
    );
  });

  const handleDelete = () => {
    if (!confirmDeleteId) return;
    deleteRegistration(
      { id: confirmDeleteId },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: [QUERY_CACHE.PRE_REGISTRATIONS_LIST, tournamentId],
          });
          setConfirmDeleteId(null);
          notify(t("preRegistration.delete.success"), "success");
        },
        onError: (error) => {
          setConfirmDeleteId(null);
          notify(t(error.error) || "Erreur inconnue", "error");
        },
      }
    );
  };

  const handleExport = async () => {
    try {
      const response = await exportPreRegistrations({ tournamentId });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `export_${selectedTournament.name}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      notify(t("preRegistration.export.error"), "error");
    }
  };

  const handleConvert = () => {
    convertPreRegistrations(
      { tournamentId },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [QUERY_CACHE.PRE_REGISTRATIONS_LIST, tournamentId],
            }),
            queryClient.invalidateQueries({
              queryKey: [QUERY_CACHE.TOURNAMENT_REGISTRATIONS, tournamentId],
            }),
          ]);
          notify(t("preRegistration.convert.success"), "success");
        },
        onError: (error) => {
          notify(t(error.error) || "Erreur inconnue", "error");
        },
      }
    );
  };

  return (
    <div className={`page ${styles.page}`}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
        }}
      >
        <h3>
          {t("preRegistration.list.title") +
            " (" +
            (searchTerm ? filteredRegistrations.length + "/" : "") +
            preRegistrations?.length +
            ")"}
        </h3>
        <span title={t("preRegistration.export")}>
          <FileSpreadsheet
            style={{ cursor: isExporting ? "wait" : "pointer" }}
            onClick={!isExporting ? handleExport : undefined}
          />
        </span>
      </div>
      <InputIcon
        icon={Search}
        placeholder={t("common.search") as string}
        value={searchTerm}
        small={true}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <button className={"button"} onClick={() => setConfirmConvert(true)} disabled={isConverting}>
        {t("preRegistration.convert")}
      </button>
      <ConfirmationModal
        isOpen={confirmConvert}
        message={t("preRegistration.convert.confirm")}
        onConfirm={() => {
          setConfirmConvert(false);
          handleConvert();
        }}
        onCancel={() => setConfirmConvert(false)}
      />
      <ConfirmationModal
        isOpen={confirmDeleteId !== null}
        message={t("preRegistration.delete.confirm")}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
      {!preRegistrations || preRegistrations.length === 0 ? (
        <p>{t("preRegistration.list.empty")}</p>
      ) : (
        <table className={"table-stripped"}>
          <thead>
            <tr>
              <th></th>
              <th>{t("preRegistration.createdDate")}</th>
              <th>{t("PLAYER_LICENCE")}</th>
              <th>{t("PLAYER_LASTNAME")}</th>
              <th>{t("PLAYER_FIRSTNAME")}</th>
              <th>{t("PLAYER_POINTS")}</th>
              <th>{t("preRegistration.heats")}</th>
              <th>{t("PLAYER_EMAIL")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredRegistrations.map((reg: PreRegistration) => (
              <tr key={reg.id}>
                <td>
                  {registrationUUID && (
                    <a
                      href={`/registration/${registrationUUID}/edit/${reg.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t("preRegistration.edit")}
                    >
                      <Pencil size={16} />
                    </a>
                  )}
                </td>
                <td>{new Date(reg.createdDate).toLocaleDateString()}</td>
                <td>{reg.licence}</td>
                <td>{reg.lastname}</td>
                <td>{reg.firstname}</td>
                <td>{reg.points}</td>
                <td>{reg.heats.map((id) => (allHeats ?? []).find((h) => h.id === id)?.name ?? id).join(", ")}</td>
                <td>{reg.email}</td>
                <td>
                  <button onClick={() => setConfirmDeleteId(reg.id)}>
                    <UserMinus fill="currentColor" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PreRegistrationListPage;
